import type React from 'react';
import {
  dispatchVerificationTest,
  getErrorTrackingStatus,
  type ErrorTrackingStatus,
  type VerificationDispatchResult,
} from '@/lib/errors/errorTrackingSink';
import { error as logError } from '@/lib/utils/logger';

type TrackingWindow = Window & typeof globalThis & {
  __stackedStreakErrorTrackingInstalled?: boolean;
  __stackedStreakErrorTrackingCleanup?: () => void;
  __stackedStreakErrorTrackingStatus?: ErrorTrackingStatus & {
    entrypoint: 'main' | 'game-main';
  };
  __stackedStreakErrorTrackingTest?: () => VerificationDispatchResult;
  __stackedStreakErrorTrackingLastVerification?: VerificationDispatchResult & {
    dispatchedAt: string;
  };
};

function normalizeUnknownError(reason: unknown, fallbackMessage: string): Error {
  if (reason instanceof Error) {
    return reason;
  }

  if (typeof reason === 'string') {
    return new Error(reason);
  }

  try {
    return new Error(JSON.stringify(reason));
  } catch {
    return new Error(fallbackMessage);
  }
}

export function captureComponentError(
  boundaryName: string,
  error: Error,
  errorInfo: Pick<React.ErrorInfo, 'componentStack'>,
  extraContext?: Record<string, unknown>
): void {
  logError(`${boundaryName} caught an error`, error, {
    component: boundaryName,
    componentStack: errorInfo.componentStack,
    ...extraContext,
  });
}

export function installGlobalErrorTracking(entrypoint: 'main' | 'game-main'): void {
  if (typeof window === 'undefined') {
    return;
  }

  const trackingWindow = window as TrackingWindow;
  if (trackingWindow.__stackedStreakErrorTrackingInstalled) {
    return;
  }

  const handleWindowError = (event: ErrorEvent) => {
    const runtimeError =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Unhandled runtime error');

    logError('Unhandled runtime error', runtimeError, {
      component: 'GlobalErrorTracking',
      action: 'window.error',
      entrypoint,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const rejectionError = normalizeUnknownError(event.reason, 'Unhandled promise rejection');

    logError('Unhandled promise rejection', rejectionError, {
      component: 'GlobalErrorTracking',
      action: 'window.unhandledrejection',
      entrypoint,
    });
  };

  trackingWindow.addEventListener('error', handleWindowError);
  trackingWindow.addEventListener('unhandledrejection', handleUnhandledRejection);

  const status = getErrorTrackingStatus();
  trackingWindow.__stackedStreakErrorTrackingStatus = {
    ...status,
    entrypoint,
  };

  if (status.invalidConfiguredSinks.length > 0 || status.missingConfiguredSinks.length > 0) {
    console.warn('Error tracking configured sinks unavailable at runtime', {
      entrypoint,
      configuredSinks: status.configuredSinks,
      invalidConfiguredSinks: status.invalidConfiguredSinks,
      missingConfiguredSinks: status.missingConfiguredSinks,
      availableSinks: status.availableSinks,
    });
  } else {
    console.info('Error tracking active sinks', {
      entrypoint,
      mode: status.mode,
      activeSinks: status.activeSinks,
    });
  }

  trackingWindow.__stackedStreakErrorTrackingInstalled = true;
  trackingWindow.__stackedStreakErrorTrackingTest = () => {
    const result = dispatchVerificationTest();
    trackingWindow.__stackedStreakErrorTrackingLastVerification = {
      ...result,
      dispatchedAt: new Date().toISOString(),
    };
    return result;
  };
  trackingWindow.__stackedStreakErrorTrackingCleanup = () => {
    trackingWindow.removeEventListener('error', handleWindowError);
    trackingWindow.removeEventListener('unhandledrejection', handleUnhandledRejection);
    trackingWindow.__stackedStreakErrorTrackingInstalled = false;
    trackingWindow.__stackedStreakErrorTrackingCleanup = undefined;
    trackingWindow.__stackedStreakErrorTrackingStatus = undefined;
    trackingWindow.__stackedStreakErrorTrackingTest = undefined;
    trackingWindow.__stackedStreakErrorTrackingLastVerification = undefined;
  };
}

export function resetGlobalErrorTrackingForTests(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const trackingWindow = window as TrackingWindow;
  trackingWindow.__stackedStreakErrorTrackingCleanup?.();
}
